<template>
    <div class="m-5">

        <div class="p-4 border rounded-xl mb-5 text-lg text-slate-800">
            <p>This is a simple comparison of the color palettes of two popular CSS frameworks, TailwindCSS and
                Bootstrap.
                A couple things to note.</p>
            <ol class="list-decimal list-inside mt-3">
                <li>TailwindCSS offers several additional colors. The palettes below are only
                    the colors the two frameworks have in common.
                </li>
                <li>
                    Bootstrap does not offer the lightest tint out
                    of the box. I generated this single additional tint with the same SASS function using a weight of
                    90%, mainly
                    just to have a matching number of tints/shades per color.
                </li>
                <li>
                    Below the comparison is a simple display of each of the default colors which is particularly interesting
                    to see when the colors of each framework are fairly different as is the case with indigo.
                </li>
            </ol>
        </div>

        <div class="hidden lg:flex">

            <div class="w-1/2">
                <h2 class="text-4xl font-extrabold tracking-tight text-sky-400">Tailwind</h2>
            </div>

            <div class="w-1/2">
                <h2 class="text-4xl font-extrabold tracking-tight" :style="{ color: '#6610f2' }">Bootstrap</h2>
            </div>

        </div>
        <!--                    <div :style="{ backgroundColor: color }" :class="getTextColorClass(color)"-->
        <!--                         class="w-16 h-32 text-sm text-center rounded-xl">-->
        <!--                        {{getHexColor(color)}}-->
        <!--                    </div>-->

        <div v-for="color in bootstrapColors" class="flex flex-wrap">

            <div class="w-full lg:w-1/2 my-2 lg:pr-4">
                <div class="flex justify-between">
                    <div class="text-slate-600 text-lg font-extrabold tracking-tight">
                        {{ formatLabel(color) }}
                    </div>

                    <div class="h-6 w-6 display lg:hidden">

                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 54 33">
                            <g clip-path="url(#prefix__clip0)">
                                <path fill="#38bdf8" fill-rule="evenodd"
                                      d="M27 0c-7.2 0-11.7 3.6-13.5 10.8 2.7-3.6 5.85-4.95 9.45-4.05 2.054.513 3.522 2.004 5.147 3.653C30.744 13.09 33.808 16.2 40.5 16.2c7.2 0 11.7-3.6 13.5-10.8-2.7 3.6-5.85 4.95-9.45 4.05-2.054-.513-3.522-2.004-5.147-3.653C36.756 3.11 33.692 0 27 0zM13.5 16.2C6.3 16.2 1.8 19.8 0 27c2.7-3.6 5.85-4.95 9.45-4.05 2.054.514 3.522 2.004 5.147 3.653C17.244 29.29 20.308 32.4 27 32.4c7.2 0 11.7-3.6 13.5-10.8-2.7 3.6-5.85 4.95-9.45 4.05-2.054-.513-3.522-2.004-5.147-3.653C23.256 19.31 20.192 16.2 13.5 16.2z"
                                      clip-rule="evenodd"/>
                            </g>
                            <defs>
                                <clipPath id="prefix__clip0">
                                    <path fill="#fff" d="M0 0h54v32.4H0z"/>
                                </clipPath>
                            </defs>
                        </svg>

                    </div>
                </div>

                <div class="flex rounded-xl overflow-hidden">
                    <div v-for="shade in shades(tailwindColorData[color])" style="width: 10%"
                         :style="getColorStyles(tailwindColorData, color, shade)"
                         class="h-28 text-xs text-center">
                        {{ tailwindColorData[color][shade] }}
                    </div>
                </div>

            </div>

            <div class="w-full lg:w-1/2 my-2 lg:pl-4">

                <div class="hidden lg:block text-slate-600 text-lg font-extrabold tracking-tight">{{
                        formatLabel(color)
                    }}
                </div>

                <div class="flex rounded-xl overflow-hidden">
                    <div v-for="shade in shades(bootstrapColorData[color])" style="width: 10%"
                         :style="getColorStyles(bootstrapColorData, color, shade)"
                         class="h-28 text-xs text-center">
                        {{ bootstrapColorData[color][shade] }}
                    </div>
                </div>

                <div class="h-6 w-6 display lg:hidden ml-auto mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" class="fill-purple-700 d-block me-2"
                         viewBox="0 0 118 94" role="img"><title>Bootstrap</title>
                        <path fill-rule="evenodd" clip-rule="evenodd"
                              d="M24.509 0c-6.733 0-11.715 5.893-11.492 12.284.214 6.14-.064 14.092-2.066 20.577C8.943 39.365 5.547 43.485 0 44.014v5.972c5.547.529 8.943 4.649 10.951 11.153 2.002 6.485 2.28 14.437 2.066 20.577C12.794 88.106 17.776 94 24.51 94H93.5c6.733 0 11.714-5.893 11.491-12.284-.214-6.14.064-14.092 2.066-20.577 2.009-6.504 5.396-10.624 10.943-11.153v-5.972c-5.547-.529-8.934-4.649-10.943-11.153-2.002-6.484-2.28-14.437-2.066-20.577C105.214 5.894 100.233 0 93.5 0H24.508zM80 57.863C80 66.663 73.436 72 62.543 72H44a2 2 0 01-2-2V24a2 2 0 012-2h18.437c9.083 0 15.044 4.92 15.044 12.474 0 5.302-4.01 10.049-9.119 10.88v.277C75.317 46.394 80 51.21 80 57.863zM60.521 28.34H49.948v14.934h8.905c6.884 0 10.68-2.772 10.68-7.727 0-4.643-3.264-7.207-9.012-7.207zM49.948 49.2v16.458H60.91c7.167 0 10.964-2.876 10.964-8.281 0-5.406-3.903-8.178-11.425-8.178H49.948z"></path>
                    </svg>
                </div>
            </div>

        </div>


        <hr class="my-5">

        <div class="w-full lg:w-1/2">

            <div class="text-slate-600 text-3xl my-5">
                For context, these are the default colors for the palettes above.
            </div>

            <div class="flex flex-wrap">
                <div v-for="color in bootstrapColors" class="mx-2">
                    <div class="text-slate-600 text-lg font-extrabold tracking-tight">
                        {{ formatLabel(color) }}
                    </div>
                    <div :style="{ backgroundColor: color }" :class="getTextColorClass(color)"
                         class="w-16 h-24 lg:w-24 lg:h-32 text-sm text-center rounded-xl">
                        {{ getHexColor(color) }}
                    </div>
                </div>
            </div>

        </div>


        <h1 class="text-7xl font-extrabold tracking-tight my-5">Additional Tailwind Colors</h1>

        <div v-for="color in extraTailwindColors" class="flex flex-wrap">

            <div class="w-full lg:w-1/2 my-2 lg:pr-4">
                <div class="flex justify-between">
                    <div class="text-slate-600 text-lg font-extrabold tracking-tight">
                        {{ formatLabel(color) }}
                    </div>

                </div>

                <div class="flex rounded-xl overflow-hidden">
                    <div v-for="shade in shades(tailwindColorData[color])" style="width: 10%"
                         :style="getColorStyles(tailwindColorData, color, shade)"
                         class="h-28 text-xs text-center">
                        {{ tailwindColorData[color][shade] }}
                    </div>
                </div>

            </div>
        </div>

        <h1 class="text-7xl font-extrabold tracking-tight my-5">Extra Colors</h1>

        <div v-for="color in extraColors" class="flex flex-wrap">

            <div class="w-full lg:w-1/2 my-2 lg:pr-4">
                <div class="flex justify-between">
                    <div class="text-slate-600 text-lg font-extrabold tracking-tight">
                        {{ formatLabel(color) }}
                    </div>

                </div>

                <div class="flex rounded-xl overflow-hidden">
                    <div v-for="shade in shades(extraColorsData[color])" style="width: 10%"
                         :style="getColorStyles(extraColorsData, color, shade)"
                         class="h-28 text-xs text-center">
                        {{ extraColorsData[color][shade] }}
                    </div>
                </div>

            </div>
        </div>

    </div>

    <!--    <div class="flex space-x-3">-->

    <!--        <div>-->
    <!--            <h2 class="text-4xl font-extrabold tracking-tight" :style="{ color: '#6610f2' }">Bootstrap Colors</h2>-->

    <!--            <div v-for="color in bootstrapColors" class="max-w-2xl">-->
    <!--                <div>{{ formatLabel(color) }}</div>-->

    <!--                <div class="flex rounded-xl overflow-hidden">-->
    <!--                    <div v-for="shade in shades(bootstrapColorData[color])"-->
    <!--                         :style="getColorStyles(bootstrapColorData, color, shade)"-->
    <!--                         class="w-16 h-24 text-sm text-center">-->
    <!--                        {{ bootstrapColorData[color][shade] }}-->
    <!--                    </div>-->
    <!--                </div>-->
    <!--            </div>-->

    <!--        </div>-->

    <!--        <div>-->
    <!--            <h2 class="text-4xl font-extrabold tracking-tight text-sky-400">Tailwind Colors</h2>-->

    <!--            <div v-for="color in bootstrapColors" class="max-w-2xl">-->
    <!--                <div>{{ formatLabel(color) }}</div>-->

    <!--                <div class="flex rounded-xl overflow-hidden">-->
    <!--                    <div v-for="shade in shades(tailwindColorData[color])"-->
    <!--                         :style="getColorStyles(tailwindColorData, color, shade)"-->
    <!--                         class="w-16 h-24 text-sm text-center">-->
    <!--                        {{ tailwindColorData[color][shade] }}-->
    <!--                    </div>-->
    <!--                </div>-->
    <!--            </div>-->

    <!--        </div>-->

    <!--            <div>-->
    <!--                <h2 class="text-4xl font-extrabold tracking-tight text-sky-400">Browser Default</h2>-->

    <!--                <div v-for="color in bootstrapColors" class="max-w-2xl">-->
    <!--                    <div>{{ formatLabel(color) }}</div>-->
    <!--                    <div :style="{ backgroundColor: color }" :class="getTextColorClass(color)"-->
    <!--                         class="w-16 h-32 text-sm text-center rounded-xl">-->
    <!--                        {{getHexColor(color)}}-->
    <!--                    </div>-->
    <!--                </div>-->

    <!--            </div>-->

    <!--    </div>-->

</template>

<script>
import StringUtil from "../../app/utils/StringUtil";
import ColorUtil from "../../app/utils/ColorUtil";
import UIUtil from "../../app/utils/UIUtil";
import BootstrapColors from '../../assets/bootstrap-colors.json'
import TailwindColors from '../../assets/tailwind-colors'
import ExtraColors from '../../assets/ui-colors-app.json'

export default {
    name: 'ColorPalettes',
    props: ['prop1', 'prop2'],
    computed: {
        bootstrapColors() {
            return Object.keys(this.bootstrapColorData)
        },
        extraColors() {
            return Object.keys(this.extraColorsData)
        },
        tailwindColors() {
            console.log('getting TW colors')
            return Object.keys(this.tailwindColorData).filter((color) => {
                return color !== 'inherit' && color !== 'current' && color !== 'transparent' && color !== 'black' &&
                        color !== 'white'
            })
        },
        extraTailwindColors() {
            console.log('getting TW colors')
            return this.tailwindColors.filter((color) => {
                return !this.bootstrapColors.includes(color)
            })
        }
    },
    data() {
        return {
                bootstrapColorData: BootstrapColors,
            tailwindColorData: TailwindColors,
            extraColorsData: ExtraColors
        }
    },
    mounted() {
        console.log('Color Palettes mounted')
    },
    methods: {
        shades(colorObject) {
            return Object.keys(colorObject)
        },
        getColorStyles(colorData, colorType, shade) {
            let textColor = Number(shade) > 300 ? colorData[colorType]['100'] : colorData[colorType]['800']

            return {
                backgroundColor: colorData[colorType][shade],
                color: textColor
            }
        },
        formatLabel(label) {
            return StringUtil.capitalize(label)
        },
        getHexColor(color) {
            let standardColor = this.formatLabel(color)
            return '#' + ColorUtil.getHexValue(standardColor)
        },
        getTextColorClass(colorName) {

            if (colorName === 'yellow' || colorName === 'cyan' || colorName === 'pink') {
                return 'text-slate-700'
            }

            return 'text-white'

            // let hexColor = this.getHexColor(colorName)
            // let uiUtil = new UIUtil()
            // let lightOrDark = uiUtil.isLightOrDark(hexColor)
            // return lightOrDark === 'light' ? 'text-slate-700' : 'text-white'
        }
    }
}
</script>
